import { Fragment, type ReactNode } from 'react'
import { en, type Messages, type Translation } from './messages'

export type Locale = 'en' | 'es-CL'

export const LOCALES: readonly { code: Locale; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es-CL', label: 'Español (Chile)' },
]

export type MessageKey = { [N in keyof Messages]: `${N & string}.${keyof Messages[N] & string}` }[keyof Messages]
type Vars = Record<string, string | number>

const STORAGE_KEY = 'locale'
// Set when the person picks a language here, so the next signed-in load saves it to their account.
const PENDING_KEY = 'localePending'
export const isLocale = (v: unknown): v is Locale => v === 'en' || v === 'es-CL'

/** Resolution order: `?lang=` (persisted, so campaign links stick), then the saved choice, then the browser's
 * primary language. The server-side prerender has no window and always renders English. */
function detect(): Locale {
  if (typeof window === 'undefined') return 'en'
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('lang')
    const q = fromQuery?.toLowerCase().startsWith('es') ? 'es-CL' : fromQuery?.toLowerCase().startsWith('en') ? 'en' : null
    if (q) {
      if (localStorage.getItem(STORAGE_KEY) !== q) localStorage.setItem(PENDING_KEY, '1')
      localStorage.setItem(STORAGE_KEY, q)
      return q
    }
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isLocale(saved)) return saved
  } catch {
    // Storage blocked (private mode, sandboxed iframe): fall through to the browser language.
  }
  return navigator.language?.toLowerCase().startsWith('es') ? 'es-CL' : 'en'
}

// English is bundled; other catalogs are separate chunks fetched only for people using that language.
const catalogs: Record<Exclude<Locale, 'en'>, () => Promise<{ default: Translation }>> = {
  'es-CL': () => import('./messages/es-CL'),
}
const loadCatalog = async (locale: Locale): Promise<Translation> => (locale === 'en' ? en : (await catalogs[locale]()).default)

// Resolved once at startup. Switching language reloads the page, so strings built at module load (nav items,
// Intl formatters) are always in the active language and nothing needs to subscribe to changes. The top-level
// await holds back every importer until the catalog is in, so the first render is already in that language.
let current: Locale = detect()
let dict: Translation = en
try {
  dict = await loadCatalog(current)
} catch {
  // Catalog chunk unavailable (offline, stale deploy): English beats a blank page, and picking the language again retries.
  current = 'en'
}

export const getLocale = (): Locale => current

/** The BCP 47 tag for Intl formatters. English keeps the Australian formats the product launched with. */
export const intlLocale = (): string => (current === 'es-CL' ? 'es-CL' : 'en-AU')

/** Whether the language came from an explicit choice (the picker or `?lang=`) rather than the browser default. */
export function hasExplicitLocale(): boolean {
  try {
    return isLocale(localStorage.getItem(STORAGE_KEY))
  } catch {
    return false
  }
}

/** True once after the person picked a language on this device; the caller saves it to their account. */
export function takePendingLocale(): boolean {
  try {
    const pending = localStorage.getItem(PENDING_KEY) === '1'
    localStorage.removeItem(PENDING_KEY)
    return pending
  } catch {
    return false
  }
}

/** Switch language and reload once its catalog is cached, so the reloaded page isn't left waiting on it.
 * `fromServer` applies the account's saved language without marking it for saving back. */
export function setLocale(locale: Locale, { fromServer = false }: { fromServer?: boolean } = {}) {
  if (locale === current) return
  let reload: () => void
  try {
    localStorage.setItem(STORAGE_KEY, locale)
    if (fromServer) localStorage.removeItem(PENDING_KEY)
    else localStorage.setItem(PENDING_KEY, '1')
    const url = new URL(window.location.href)
    url.searchParams.delete('lang')
    reload = () => window.location.replace(url)
  } catch {
    // Without storage the choice can't survive the reload; fall back to the query parameter.
    const url = new URL(window.location.href)
    url.searchParams.set('lang', locale)
    reload = () => window.location.assign(url)
  }
  loadCatalog(locale).then(reload, () => undefined)
}

const interpolate = (s: string, vars?: Vars) => (vars ? s.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m)) : s)

function lookup(key: MessageKey): string {
  const dot = key.indexOf('.')
  const ns = key.slice(0, dot) as keyof Messages
  const name = key.slice(dot + 1)
  return (dict[ns] as Record<string, string>)[name] ?? (en[ns] as Record<string, string>)[name] ?? key
}

/** Translate `namespace.key`, replacing `{name}` placeholders from `vars`. Numbers are inserted as-is: format
 * them first (formatNumber, formatMoney…) when they need thousands separators. */
export function t(key: MessageKey, vars?: Vars): string {
  return interpolate(lookup(key), vars)
}

/** Translate a message whose parts need markup, e.g. `'Read our <terms>terms</terms>'` with
 * `{ terms: (text) => <Link to="/terms">{text}</Link> }`. Tags don't nest; `{name}` placeholders work too. */
export function tRich(key: MessageKey, tags: Record<string, (text: string) => ReactNode>, vars?: Vars): ReactNode {
  const s = interpolate(lookup(key), vars)
  const out: ReactNode[] = []
  const re = /<(\w+)>(.*?)<\/\1>/g
  let last = 0
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push(s.slice(last, m.index))
    const render = tags[m[1]]
    out.push(<Fragment key={m.index}>{render ? render(m[2]) : m[2]}</Fragment>)
    last = m.index + m[0].length
  }
  if (last < s.length) out.push(s.slice(last))
  return out
}

export const formatNumber = (n: number, options?: Intl.NumberFormatOptions) => new Intl.NumberFormat(intlLocale(), options).format(n)
